import _get from "lodash-es/get";
import * as formSelectors from "src/ipm-shared/components/Form/selectors";
import { COUNTRY_OPTION_FORM } from "src/ipm-shared/components/SwitchCountryControl/const";
import {
  ALL_ALLOWED_PURPOSES,
  ALLOWED_INT_CARD_BRANDS,
  ALLOWED_INT_PURPOSES,
  ALLOWED_INT_STATUSES,
  INTERNATIONAL_CURRENCY_TO_COUNTRY_ID,
  NOT_ALLOWED_NORMAL_PURPOSES,
  SEARCH_FORM,
  UAPI_CURRENCY_IDS
} from "./const";

// Mirrors selectors.getPurposeIdFilter(state, false) without importing the
// Payment selectors module (which registers reducers / pulls in the store and
// is unnecessary — and untestable — for building a query from form controls).
const getBasePurposeIdFilter = (state: any) => {
  let purposeId = formSelectors.getControl(state, "purpose_id").value;
  if (purposeId) {
    purposeId = (purposeId as string).replace(/-/g, ",");
  } else {
    purposeId = ALL_ALLOWED_PURPOSES.join(",");
  }
  return purposeId;
};

/**
 * Builds the admin payments query for the International / UAPI export. The UAPI
 * Approve call MUST send exactly the same filters, so it reuses this builder
 * (see `handleAdminDbsUapiApprove` in sagas.ts). INT allowed statuses and card
 * brands are applied to both; the INT tab uses international purposes, the UAPI
 * tab domestic ones (no "international" in purpose_id).
 *
 * On the UAPI tab `dbs_uapi_only` is added, the payout country is left to the
 * server-side whitelist, and one of SGD/HKD/USD is always sent as currency_id.
 */
export function buildAdminInternationalExportQuery(
  state: any,
  opts: { isUapi: boolean }
) {
  const query = formSelectors.getControlsAsObject(state, SEARCH_FORM) as any;
  const queryCountry = formSelectors.getControlsAsObject(
    state,
    COUNTRY_OPTION_FORM
  ) as any;
  const currencyId = _get(queryCountry, "switch_control_currency_id", 0);
  const controlValue = _get(queryCountry, "switch_control_value", 0);

  let purposeId = getBasePurposeIdFilter(state);
  let paymentStatusId = formSelectors.getControl(state, "payment_status_id")
    .value;
  if (paymentStatusId) {
    paymentStatusId = (paymentStatusId as string).replace(/-/g, ",");
  }

  if (paymentStatusId) {
    const allowedStatuses = ALLOWED_INT_STATUSES.map(String);
    paymentStatusId = (paymentStatusId as string)
      .split(",")
      .filter(s => allowedStatuses.includes(s))
      .join(",");
  }

  if (opts.isUapi) {
    // UAPI uses domestic (SG tab style) purposes, without "international".
    if (purposeId) {
      const notAllowedTypes = NOT_ALLOWED_NORMAL_PURPOSES.map(String);
      purposeId = (purposeId as string)
        .split(",")
        .filter(t => !notAllowedTypes.includes(t))
        .join(",");
    }
  } else {
    if (purposeId) {
      const allowedTypes = ALLOWED_INT_PURPOSES.map(String);
      purposeId = (purposeId as string)
        .split(",")
        .filter(t => allowedTypes.includes(t))
        .join(",");
    }
    purposeId = purposeId ? `international,${purposeId}` : "international";
  }

  if (query.card_type) {
    const allowedBrands = ALLOWED_INT_CARD_BRANDS.map(String);
    query.card_type = (query.card_type as string)
      .split(",")
      .filter(c => allowedBrands.includes(c))
      .join(",");
  }

  query.purpose_id = purposeId;
  query.payment_status_id = paymentStatusId;

  if (opts.isUapi) {
    query.dbs_uapi_only = "true";
    // UAPI covers SGD/HKD/USD and the list API takes a single currency_id;
    // anything else (e.g. left over from another tab) falls back to SGD.
    query.currency_id = UAPI_CURRENCY_IDS.includes(Number(currencyId))
      ? Number(currencyId)
      : UAPI_CURRENCY_IDS[0];
    // Whitelist is applied server-side; do not constrain by country.
    delete query.country_id;
  } else {
    query.currency_id = currencyId || 1;
    query.country_id = INTERNATIONAL_CURRENCY_TO_COUNTRY_ID[currencyId] || 1;
  }

  if (controlValue === "CITIBANK" || controlValue === "CHEFSTATION") {
    query.partnership = controlValue;
  }

  return query;
}
